import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRAND, HERO_IMG } from "../../data/content";
import { MagneticButton } from "../Magnetic";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] w-full overflow-hidden bg-navy-900">
      {/* Parallax backdrop */}
      <motion.div style={{ y: yImg }} className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 via-navy-900/60 to-navy-900" />
      </motion.div>

      {/* Blueprint line-draw overlay */}
      <svg className="absolute inset-0 h-full w-full opacity-[0.18]" preserveAspectRatio="none" viewBox="0 0 1440 900" fill="none">
        {[
          "M0 220 H1440", "M0 680 H1440", "M360 0 V900", "M1080 0 V900",
          "M360 220 L1080 680", "M1080 220 L360 680",
        ].map((d, i) => (
          <motion.path
            key={i}
            d={d}
            stroke="#D4AF37"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2.2, ease, delay: 0.3 + i * 0.15 }}
          />
        ))}
        {[[360, 220], [1080, 220], [360, 680], [1080, 680]].map(([cx, cy], i) => (
          <motion.circle
            key={i} cx={cx} cy={cy} r="5" fill="#D4AF37"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ delay: 1.4 + i * 0.1, duration: 0.5 }}
          />
        ))}
      </svg>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-center px-6 sm:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease }}
          className="mb-6 text-[11px] uppercase tracking-[0.4em] text-gold"
        >
          Turnkey Construction · Interiors · Fit-out — Delhi NCR
        </motion.p>

        <h1 className="max-w-5xl font-heading text-[15vw] font-light leading-[0.92] tracking-tight text-offwhite sm:text-7xl lg:text-8xl">
          {["Building", "stronger", "tomorrows,"].map((w, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ delay: 0.7 + i * 0.12, duration: 0.9, ease }}
              >
                {w}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden">
            <motion.span
              className="inline-block italic text-gold"
              initial={{ y: "110%" }} animate={{ y: 0 }}
              transition={{ delay: 1.05, duration: 0.9, ease }}
            >
              together.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 flex flex-col items-start gap-8 sm:flex-row sm:items-center"
        >
          <p className="max-w-md text-sm font-light leading-relaxed text-offwhite/70 sm:text-base">
            {BRAND.positioning}
          </p>
          <MagneticButton href="#projects" data-testid="hero-cta">
            View the work
          </MagneticButton>
        </motion.div>
      </motion.div>

      <motion.a
        href="#about"
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-gold"
      >
        <span className="text-[9px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.a>
    </section>
  );
}
